// ============================================================
// Game — main orchestrator
// ============================================================
import * as PIXI from 'pixi.js';
import { LEVELS } from './data/levels.js';
import { TankCharacter } from './entities/TankCharacter.js';
import { Platform, Hazard, Goal, Collectible, DialogNPC } from './entities/LevelObjects.js';
import { SpawnedEntity } from './entities/SpawnedEntity.js';
import { PhysicsEngine } from './physics/PhysicsEngine.js';
import { WordSystem } from './systems/WordSystem.js';
import { Camera } from './systems/Camera.js';
import { HUD } from './ui/HUD.js';
import { LevelSelect } from './ui/LevelSelect.js';
import { WordInput } from './ui/WordInput.js';
import { GAME_WIDTH, GAME_HEIGHT, STATE, COLOR, Z } from './constants.js';
import { sound } from './systems/SoundEngine.js';
import { InteractionSystem } from './systems/InteractionSystem.js';
import { DragSystem } from './systems/DragSystem.js';

export class Game {
  constructor() {
    this.app   = null;
    this.state = STATE.MENU;

    // Layers
    this.worldContainer = null;  // scrolls with camera
    this.bgContainer    = null;  // parallax background
    this.uiContainer    = null;  // fixed UI

    // Systems
    this.physics = new PhysicsEngine();
    this.camera  = new Camera();

    // Entities
    this.player      = null;
    this.platforms   = [];
    this.hazards     = [];
    this.goals       = [];
    this.collectibles = [];
    this.npcs        = [];

    // Systems
    this.wordSystem      = null;
    this.interactionSys  = null;
    this.dragSystem      = null;

    // UI
    this.hud         = null;
    this.levelSelect = null;
    this.wordInput   = null;

    // Level state
    this.currentLevelIdx = -1;
    this.score = 0;
    this._risingLavaY = null;
    this._risingLavaGfx = null;
    this._lavaSpeed = 0;

    // Input
    this._keys = {};
    this._boundKeyDown = this._onKeyDown.bind(this);
    this._boundKeyUp   = this._onKeyUp.bind(this);
  }

  async init() {
    // ── Create PIXI app ──
    this.app = new PIXI.Application();
    await this.app.init({
      width:  GAME_WIDTH,
      height: GAME_HEIGHT,
      backgroundColor: COLOR.skyDeep,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    const canvas = this.app.canvas;
    canvas.style.width  = '100%';
    canvas.style.height = '100%';
    canvas.style.maxWidth  = `${GAME_WIDTH}px`;
    canvas.style.maxHeight = `${GAME_HEIGHT}px`;
    document.getElementById('gameContainer').appendChild(canvas);

    // ── Containers ──
    this.bgContainer    = new PIXI.Container();
    this.bgContainer.zIndex = -1;

    this.worldContainer = new PIXI.Container();
    this.worldContainer.sortableChildren = true;

    this.uiContainer    = new PIXI.Container();
    this.uiContainer.zIndex = Z.ui;

    this.app.stage.addChild(this.bgContainer);
    this.app.stage.addChild(this.worldContainer);
    this.app.stage.addChild(this.uiContainer);
    this.app.stage.sortableChildren = true;

    // ── Word system ──
    this.wordSystem = new WordSystem(this.physics, this.worldContainer);
    this.wordSystem.onSpawn = (entity, config, label) => {
      // Keep drag+interaction systems in sync after each spawn
      if (this.dragSystem)     this.dragSystem.entities     = this.wordSystem.entities;
      if (this.interactionSys) this.interactionSys.entities = this.wordSystem.entities;
    };

    // ── UI ──
    this.hud = new HUD(this.uiContainer);

    this.wordInput = new WordInput(
      this.uiContainer,
      (val) => {
        if (!this.player) return;
        const result = this.wordSystem.prepare(val);
        if (!result.success) {
          this.hud.showSpawnMsg(result.msg);
          sound.errorBeep();
          return;
        }
        // Enter placement mode — ghost follows cursor, click to place
        this.dragSystem?.startPlacement(result.config);
        this.hud.showSpawnMsg(
          `✨ ${result.config.emoji ?? ''} ${result.label} — click anywhere to place it!`
        );
        sound.spawn(result.config);
        if (result.config.modLabel) sound.spawnModified(result.config.modLabel);
      },
      (partial) => this.wordSystem.getSuggestions(partial),
      this.app.canvas,   // pass canvas so input can calc correct screen position
    );

    this.levelSelect = new LevelSelect(
      this.uiContainer,
      (idx) => this.startLevel(idx),
      () => {},
    );

    // ── Input listeners ──
    window.addEventListener('keydown', this._boundKeyDown);
    window.addEventListener('keyup',   this._boundKeyUp);

    // ── Drag system (placement + drag-to-move) ──
    this.dragSystem = new DragSystem(this.app, this.worldContainer, this.app.canvas);
    this.dragSystem.entities = this.wordSystem?.entities ?? [];
    this.dragSystem.hud      = this.hud;   // trash can integration

    this.dragSystem.onPlace  = (config, wx, wy) => {
      const entity = this.wordSystem.spawnAt(config, wx, wy, config.word ?? config.type);
      this.hud.showSpawnMsg(`✨ ${config.emoji ?? ''} placed!`);
      this.interactionSys.entities = this.wordSystem.entities;
      this.dragSystem.entities     = this.wordSystem.entities;
    };
    this.dragSystem.onCancel = () => {
      this.hud.showSpawnMsg('Placement cancelled');
    };
    this.dragSystem.onDelete = (entity) => {
      // Poof particles then remove
      this._spawnPoofAt(entity.body.x, entity.body.y);
      this.wordSystem.removeEntity(entity);
      this.hud.showSpawnMsg('🗑️ Deleted!');
    };

    // ── Sound engine — show a "click to start" splash first ──
    this._showStartSplash();

    // ── Main loop ──
    this.app.ticker.add((ticker) => {
      const dt = Math.min(ticker.deltaMS / 1000, 0.05); // cap at 50ms
      this._update(dt);
    });

    // Level select shown after splash (see _showStartSplash)
  }

  // ── Level loading ───────────────────────────────────────

  async startLevel(idx) {
    this.state = STATE.LOADING;
    this._cleanup();

    const levelData = LEVELS[idx];
    this.currentLevelIdx = idx;

    this.levelSelect?.hide();

    // ── Draw background ──
    this._buildBackground(levelData);

    // ── Build world geometry ──
    for (const spec of levelData.platforms) {
      const plat = new Platform(spec, this.worldContainer);
      this.physics.addBody(plat.body);
      this.platforms.push(plat);
    }

    // ── Hazards ──
    for (const spec of levelData.hazards ?? []) {
      const haz = new Hazard(spec, this.worldContainer);
      this.physics.addBody(haz.body);
      this.hazards.push(haz);
    }

    // ── Water zones ──
    this.physics.waterZones = [];
    for (const wz of levelData.water ?? []) {
      this.physics.addWaterZone(wz.x, wz.y, wz.w, wz.h);
      this._drawWater(wz);
    }

    // ── Wind zones ──
    this.physics.windZones = [];
    for (const wz of levelData.windZones ?? []) {
      this.physics.addWindZone(wz.x, wz.y, wz.w, wz.h, wz.fx, wz.fy);
    }

    // ── Goal ──
    const goalSpec = levelData.goal;
    const goal = new Goal(goalSpec, this.worldContainer, () => this._onLevelComplete());
    this.physics.addBody(goal.body);
    this.goals.push(goal);

    // ── Collectibles ──
    for (const spec of levelData.collectibles ?? []) {
      const col = new Collectible(spec, this.worldContainer, (score, s) => {
        this.score += score;
        this.hud.setScore(this.score);
        this.hud.showSpawnMsg(`+${score} 💎`);
        if (s.type === 'pearl') sound.pearl();
        else sound.collect();
      });
      this.physics.addBody(col.body);
      this.collectibles.push(col);
    }

    // ── NPCs ──
    for (const spec of levelData.npcs ?? []) {
      const npc = new DialogNPC(spec, this.worldContainer);
      // No physics for dialog NPCs, just visuals
      this.npcs.push(npc);
    }

    // ── Rising lava (Level 4) ──
    if (levelData.risingLava) {
      const { startY, speed } = levelData.risingLava;
      this._risingLavaY = startY;
      this._lavaSpeed   = speed;
      this._risingLavaGfx = new PIXI.Graphics();
      this._risingLavaGfx.zIndex = Z.hazards - 1;
      this.worldContainer.addChild(this._risingLavaGfx);
    }

    // ── Player ──
    this.player = new TankCharacter(this.worldContainer);
    await this.player.load();
    this.physics.addBody(this.player.body);
    this.player.setPosition(levelData.start.x, levelData.start.y - 60);
    this.player.lives = 3;
    this.player.score = 0;

    // Give word system a reference to the player for AI / balloon
    this.wordSystem.player = this.player;

    // Interaction system (Scribblenauts-style automatic reactions)
    this.interactionSys = new InteractionSystem(
      this.wordSystem.entities,
      this.player,
      (msg) => this.hud.showMessage(msg, 2.5),
    );
    // Give interaction system the level's world bounds so floating
    // objects bounce at the edges instead of drifting off forever.
    this.interactionSys.worldBounds = { minX: 0, maxX: levelData.worldW };

    // Keep drag system entity list in sync
    this.dragSystem.entities = this.wordSystem.entities;

    // ── Camera ──
    this.camera.setWorldBounds(levelData.worldW, levelData.worldH);
    this.camera.snapTo(levelData.start.x, levelData.start.y);

    // ── HUD ──
    this.hud.setLevel(levelData);
    this.hud.setScore(this.score);
    this.hud.setLives(this.player.lives);
    this.hud.showMessage(`Level ${levelData.id}: ${levelData.name}`, 3.0);

    this.wordSystem.clear();

    this.state = STATE.PLAYING;
    sound.startMusic(levelData.background ?? 'beach');
  }

  _buildBackground(levelData) {
    this.bgContainer.removeChildren();

    const bg = levelData.background;
    const gradient = new PIXI.Graphics();

    // Sky gradient (approximated with layered rects)
    const skyColors = {
      beach:   [[0x87CEEB, 0.5], [0xE0F0FF, 0.5]],
      reef:    [[0x006994, 0.4], [0x00BCD4, 0.6]],
      temple:  [[0x2C3E50, 0.4], [0x34495E, 0.6]],
      volcano: [[0x4E0000, 0.3], [0xFF6D00, 0.7]],
      sky:     [[0x1565C0, 0.3], [0x82B1FF, 0.7]],
    };
    const colors = skyColors[bg] ?? skyColors.beach;
    const h1 = GAME_HEIGHT * colors[0][1];
    gradient.rect(0, 0, GAME_WIDTH, h1).fill({ color: colors[0][0] });
    gradient.rect(0, h1, GAME_WIDTH, GAME_HEIGHT - h1).fill({ color: colors[1][0] });
    this.bgContainer.addChild(gradient);

    // Decorative elements
    if (bg === 'beach') this._drawBeachBg();
    if (bg === 'reef')  this._drawReefBg();
    if (bg === 'sky')   this._drawSkyBg();
    if (bg === 'volcano') this._drawVolcanoBg();
    if (bg === 'temple')  this._drawTempleBg();
  }

  _drawBeachBg() {
    // Sun
    const sun = new PIXI.Graphics();
    sun.circle(0, 0, 60).fill({ color: 0xFFD700 });
    sun.circle(0, 0, 70).fill({ color: 0xFFE082, alpha: 0.4 });
    sun.x = GAME_WIDTH * 0.8;
    sun.y = GAME_HEIGHT * 0.18;
    this.bgContainer.addChild(sun);

    // Clouds
    for (let i = 0; i < 4; i++) {
      const cloud = new PIXI.Graphics();
      cloud.ellipse(0, 0, 80, 35).fill({ color: 0xFFFFFF, alpha: 0.85 });
      cloud.ellipse(50, -8, 55, 28).fill({ color: 0xFFFFFF, alpha: 0.85 });
      cloud.ellipse(-50, -5, 50, 25).fill({ color: 0xFFFFFF, alpha: 0.85 });
      cloud.x = 150 + i * 310;
      cloud.y = 80 + i * 30;
      this.bgContainer.addChild(cloud);
    }

    // Sand dunes (static decoration)
    const dunes = new PIXI.Graphics();
    dunes.ellipse(200, GAME_HEIGHT, 300, 80).fill({ color: 0xF5DEB3, alpha: 0.5 });
    dunes.ellipse(700, GAME_HEIGHT, 400, 60).fill({ color: 0xF5DEB3, alpha: 0.4 });
    this.bgContainer.addChild(dunes);
  }

  _drawReefBg() {
    // Underwater rays
    for (let i = 0; i < 6; i++) {
      const ray = new PIXI.Graphics();
      ray.poly([
        0, 0,
        40, GAME_HEIGHT,
        0, GAME_HEIGHT,
        -20, 0,
      ]).fill({ color: 0xB3E5FC, alpha: 0.08 });
      ray.x = 100 + i * 200;
      ray.y = 0;
      this.bgContainer.addChild(ray);
    }
    // Coral silhouettes
    for (let i = 0; i < 8; i++) {
      const coral = new PIXI.Graphics();
      const h = 60 + Math.random() * 80;
      coral.rect(-6, -h, 12, h).fill({ color: 0xFF6B6B, alpha: 0.3 });
      coral.circle(-12, -h, 8).fill({ color: 0xFF6B6B, alpha: 0.3 });
      coral.circle(0, -h - 8, 10).fill({ color: 0xFF6B6B, alpha: 0.3 });
      coral.circle(12, -h, 8).fill({ color: 0xFF6B6B, alpha: 0.3 });
      coral.x = 60 + i * 160;
      coral.y = GAME_HEIGHT;
      this.bgContainer.addChild(coral);
    }
  }

  _drawSkyBg() {
    // Stars
    for (let i = 0; i < 50; i++) {
      const star = new PIXI.Graphics();
      star.circle(0, 0, 2 + Math.random() * 3).fill({ color: 0xFFFFFF, alpha: 0.6 + Math.random() * 0.4 });
      star.x = Math.random() * GAME_WIDTH;
      star.y = Math.random() * GAME_HEIGHT * 0.6;
      this.bgContainer.addChild(star);
    }
    // Moon
    const moon = new PIXI.Graphics();
    moon.circle(0, 0, 50).fill({ color: 0xFFF9C4 });
    moon.circle(15, -10, 40).fill({ color: 0x1565C0 }); // crescent
    moon.x = GAME_WIDTH * 0.15;
    moon.y = GAME_HEIGHT * 0.15;
    this.bgContainer.addChild(moon);
  }

  _drawVolcanoBg() {
    // Volcano silhouette
    const vol = new PIXI.Graphics();
    vol.poly([200, GAME_HEIGHT, 500, 100, 800, GAME_HEIGHT]).fill({ color: 0x3E2000, alpha: 0.7 });
    this.bgContainer.addChild(vol);
    // Lava glow at top
    const glow = new PIXI.Graphics();
    glow.circle(500, 100, 120).fill({ color: 0xFF6D00, alpha: 0.3 });
    this.bgContainer.addChild(glow);
    // Ash particles (simplified)
    for (let i = 0; i < 20; i++) {
      const ash = new PIXI.Graphics();
      ash.circle(0, 0, 4 + Math.random() * 6).fill({ color: 0x424242, alpha: 0.5 });
      ash.x = Math.random() * GAME_WIDTH;
      ash.y = Math.random() * GAME_HEIGHT;
      this.bgContainer.addChild(ash);
    }
  }

  _drawTempleBg() {
    // Temple columns
    for (let i = 0; i < 5; i++) {
      const col = new PIXI.Graphics();
      col.rect(-15, 0, 30, GAME_HEIGHT).fill({ color: 0x455A64, alpha: 0.5 });
      col.x = 80 + i * 280;
      this.bgContainer.addChild(col);
    }
    // Ceiling decoration
    const ceil = new PIXI.Graphics();
    ceil.rect(0, 0, GAME_WIDTH, 30).fill({ color: 0x37474F, alpha: 0.8 });
    this.bgContainer.addChild(ceil);
  }

  _drawWater(wz) {
    const water = new PIXI.Graphics();
    water.rect(wz.x, wz.y, wz.w, wz.h).fill({ color: 0x0288D1, alpha: 0.55 });
    // Surface shimmer
    water.rect(wz.x, wz.y, wz.w, 8).fill({ color: 0x80DEEA, alpha: 0.6 });
    water.zIndex = Z.platforms - 1;
    this.worldContainer.addChild(water);
  }

  // ── Main update ─────────────────────────────────────────

  _update(dt) {
    if (this.state !== STATE.PLAYING) return;

    // ── Physics ──
    this.physics.update(dt);

    // ── Player ──
    const wasOnGround = this.player.body.onGround;
    const wasVy       = this.player.body.vy;
    this.player.update(dt);

    // ── Sound: walk / land / jump ──
    sound.tickWalk(dt, this.player.body.vx, this.player.body.onGround);
    if (!wasOnGround && this.player.body.onGround && wasVy > 80) sound.land();

    // ── Player controls ──
    const prevJump = this.player.keys.jump;
    this.player.keys.left  = !!this._keys['ArrowLeft']  || !!this._keys['a'] || !!this._keys['A'];
    this.player.keys.right = !!this._keys['ArrowRight'] || !!this._keys['d'] || !!this._keys['D'];
    this.player.keys.jump  = !!this._keys['ArrowUp']    || !!this._keys['w'] || !!this._keys['W'] || !!this._keys[' '];
    // Jump sound fires on rising edge
    if (this.player.keys.jump && !prevJump && this.player.body.onGround) sound.jump();

    // ── Rising lava ──
    if (this._risingLavaY !== null) {
      this._risingLavaY -= this._lavaSpeed * dt;
      this._drawRisingLava();
      // Kill player if touched
      if (this.player.body.bottom > this._risingLavaY) {
        this.player.hurt();
        sound.hurt();
      }
      // Danger warning when lava gets close
      const lavaGap = this._risingLavaY - this.player.body.bottom;
      if (lavaGap < 120 && Math.random() < 0.012) sound.lavaWarning();
    }

    // ── Platforms (moving) ──
    for (const p of this.platforms) p.update(dt);

    // ── Hazards ──
    for (const h of this.hazards) h.update(dt);

    // ── Goals ──
    for (const g of this.goals) g.update(dt);

    // ── Collectibles ──
    for (const c of this.collectibles) c.update(dt);

    // ── NPCs ──
    for (const n of this.npcs) n.update(dt);

    // ── Word system ──
    this.wordSystem.update(dt);

    // ── Interaction system (auto Scribblenauts reactions) ──
    this.interactionSys.update(dt);

    // ── HUD ──
    this.hud.update(dt);
    this.hud.setLives(this.player.lives);

    // ── Camera ──
    this.camera.follow(this.player.body, dt);
    this.camera.applyTo(this.worldContainer, dt);

    // ── Check NPC proximity for dialog ──
    for (const npc of this.npcs) {
      if (!npc.spec.dialog) continue;
      const dx = this.player.body.x - npc.spec.x;
      const dy = this.player.body.y - npc.spec.y;
      if (Math.sqrt(dx*dx + dy*dy) < 80 && Math.random() < 0.005) {
        npc.showDialog();
      }
    }

    // ── Wind ambient sound ──
    if (this.physics.windZones.length > 0) {
      const inWind = this.physics.windZones.some(wz =>
        this.player.body.x > wz.x && this.player.body.x < wz.x + wz.w &&
        this.player.body.y > wz.y && this.player.body.y < wz.y + wz.h
      );
      if (inWind && Math.random() < 0.008) sound.wind();
    }

    // ── Boundary kill (fall off world) ──
    const level = LEVELS[this.currentLevelIdx];
    if (this.player.body.y > level.worldH + 200 || this.player.body.y < -400) {
      this.player.hurt();
      if (!this.player.dead) {
        this.player.respawn(level.start.x, level.start.y - 60);
      }
    }

    // ── Game over ──
    if (this.player.dead) {
      this.hud.showMessage('💀 Game Over! Press R to retry.', 60);
    }

    // ── Player hurt (hazard collisions handled here for sound) ──
    if (this.player._hurtTimer > 0 && this.player._hurtTimer > this._prevHurtTimer) {
      sound.hurt();
    }
    this._prevHurtTimer = this.player._hurtTimer ?? 0;

    // ── Time up ──
    if (this.hud.isTimeUp()) {
      this.hud.showMessage('⏰ Time\'s up! Press R to retry!', 60);
    }
  }

  _drawRisingLava() {
    if (!this._risingLavaGfx) return;
    const level = LEVELS[this.currentLevelIdx];
    const h = level.worldH - this._risingLavaY;
    this._risingLavaGfx.clear();
    this._risingLavaGfx.rect(0, this._risingLavaY, level.worldW, h + 100)
      .fill({ color: 0xFF4500, alpha: 0.88 });
    // Lava surface bubbles
    const t = Date.now() / 500;
    for (let i = 0; i < 8; i++) {
      const bx = (level.worldW / 8) * (i + 0.5) + Math.sin(t + i) * 20;
      const by = this._risingLavaY + Math.sin(t * 2 + i * 1.3) * 12;
      this._risingLavaGfx.circle(bx, by, 10 + Math.sin(t + i) * 5)
        .fill({ color: 0xFFAA00, alpha: 0.7 });
    }
  }

  // ── Win / Lose ──────────────────────────────────────────

  _onLevelComplete() {
    if (this.state !== STATE.PLAYING) return;
    this.state = STATE.LEVEL_COMPLETE;

    const level = LEVELS[this.currentLevelIdx];
    this.player.celebrate();
    this.camera.addShake(0.3);
    sound.stopMusic();
    sound.levelComplete();

    // Celebration overlay
    setTimeout(() => {
      this._showCompletionScreen(level);
    }, 1500);
  }

  _showCompletionScreen(level) {
    const overlay = new PIXI.Container();
    overlay.zIndex = Z.ui + 50;
    this.uiContainer.addChild(overlay);

    const bg = new PIXI.Graphics();
    bg.rect(0, 0, GAME_WIDTH, GAME_HEIGHT).fill({ color: 0x000000, alpha: 0.7 });
    overlay.addChild(bg);

    const panel = new PIXI.Graphics();
    panel.roundRect(240, 160, 800, 400, 24)
      .fill({ color: 0x0D1B2A, alpha: 0.97 });
    panel.roundRect(240, 160, 800, 400, 24)
      .stroke({ color: 0xFFD700, width: 4 });
    overlay.addChild(panel);

    const title = new PIXI.Text({
      text: '🎉 Level Complete! 🎉',
      style: { fontSize: 46, fill: 0xFFD700, fontWeight: 'bold',
               stroke: { color: 0x8B4513, width: 5 } },
    });
    title.anchor.set(0.5, 0);
    title.x = GAME_WIDTH / 2;
    title.y = 190;
    overlay.addChild(title);

    const unlockMsg = new PIXI.Text({
      text: level.unlockMessage,
      style: { fontSize: 22, fill: 0xB0C4DE, fontStyle: 'italic', align: 'center', wordWrap: true, wordWrapWidth: 700 },
    });
    unlockMsg.anchor.set(0.5, 0);
    unlockMsg.x = GAME_WIDTH / 2;
    unlockMsg.y = 270;
    overlay.addChild(unlockMsg);

    const scoreText = new PIXI.Text({
      text: `Score: ${this.score}`,
      style: { fontSize: 36, fill: 0x1DE9B6, fontWeight: 'bold' },
    });
    scoreText.anchor.set(0.5, 0);
    scoreText.x = GAME_WIDTH / 2;
    scoreText.y = 330;
    overlay.addChild(scoreText);

    // Buttons
    const makeBtn = (label, color, x, y, cb) => {
      const btn = new PIXI.Container();
      btn.interactive = true;
      btn.cursor = 'pointer';
      const bg2 = new PIXI.Graphics();
      bg2.roundRect(-110, -24, 220, 48, 12).fill({ color });
      bg2.roundRect(-110, -24, 220, 48, 12).stroke({ color: 0xFFFFFF, alpha: 0.3, width: 2 });
      const txt = new PIXI.Text({ text: label, style: { fontSize: 22, fill: 0xFFFFFF, fontWeight: 'bold' } });
      txt.anchor.set(0.5);
      btn.addChild(bg2);
      btn.addChild(txt);
      btn.x = x; btn.y = y;
      btn.on('pointertap', cb);
      btn.on('pointerover', () => btn.scale.set(1.08));
      btn.on('pointerout',  () => btn.scale.set(1.0));
      overlay.addChild(btn);
    };

    const nextIdx = this.currentLevelIdx + 1;
    if (nextIdx < LEVELS.length) {
      makeBtn('▶ Next Level', 0x388E3C, GAME_WIDTH / 2 - 130, 430, () => {
        overlay.destroy();
        this.startLevel(nextIdx);
      });
    }

    makeBtn('📋 Level Select', 0x1565C0, GAME_WIDTH / 2 + 130, 430, () => {
      overlay.destroy();
      this._cleanup();
      this._showLevelSelect();
    });
  }

  // ── Start splash (audio unlock) ─────────────────────────

  _showStartSplash() {
    const splash = new PIXI.Container();
    splash.zIndex = 999;
    this.app.stage.addChild(splash);

    // Full dark background
    const bg = new PIXI.Graphics();
    bg.rect(0, 0, GAME_WIDTH, GAME_HEIGHT).fill({ color: 0x05101A });
    splash.addChild(bg);

    // Animated turtle emoji title
    const title = new PIXI.Text({
      text: '🐢 Magic Turtle Words 🐢',
      style: {
        fontSize: 58,
        fontFamily: 'Georgia, serif',
        fill: 0xFFD700,
        fontWeight: 'bold',
        stroke: { color: 0x8B4513, width: 6 },
        dropShadow: { color: 0x000000, blur: 12, distance: 4 },
      },
    });
    title.anchor.set(0.5);
    title.x = GAME_WIDTH / 2;
    title.y = GAME_HEIGHT / 2 - 80;
    splash.addChild(title);

    // Pulsing click-to-start button
    const btnBg = new PIXI.Graphics();
    btnBg.roundRect(-220, -38, 440, 76, 38).fill({ color: 0x1DE9B6 });
    btnBg.roundRect(-220, -38, 440, 76, 38).stroke({ color: 0xFFFFFF, alpha: 0.5, width: 3 });
    const btnText = new PIXI.Text({
      text: '🔊  Click Here to Start!',
      style: {
        fontSize: 30,
        fill: 0x05101A,
        fontWeight: 'bold',
        fontFamily: 'Georgia, serif',
      },
    });
    btnText.anchor.set(0.5);

    const btn = new PIXI.Container();
    btn.addChild(btnBg);
    btn.addChild(btnText);
    btn.x = GAME_WIDTH / 2;
    btn.y = GAME_HEIGHT / 2 + 50;
    btn.interactive = true;
    btn.cursor = 'pointer';
    splash.addChild(btn);

    // Subtitle
    const sub = new PIXI.Text({
      text: 'Browsers require a click before playing audio',
      style: { fontSize: 16, fill: 0x607D8B, fontStyle: 'italic' },
    });
    sub.anchor.set(0.5);
    sub.x = GAME_WIDTH / 2;
    sub.y = GAME_HEIGHT / 2 + 115;
    splash.addChild(sub);

    // Pulse animation
    let t = 0;
    const pulse = (ticker) => {
      t += ticker.deltaMS / 1000;
      btn.scale.set(0.96 + Math.sin(t * 3) * 0.04);
      title.y = GAME_HEIGHT / 2 - 80 + Math.sin(t * 1.5) * 6;
    };
    this.app.ticker.add(pulse);

    // Declare both handlers up front so they can reference each other.
    let keyHandler;

    // Click handler — THIS is the direct user gesture
    const onClick = async () => {
      // Always remove the keyboard listener whether user clicked or
      // pressed a key — prevents it firing again during gameplay.
      window.removeEventListener('keydown', keyHandler);
      this.app.ticker.remove(pulse);
      splash.destroy({ children: true });

      // Resume AudioContext from within the click handler
      const ok = await sound.init();
      console.log('[Sound] AudioContext state:', sound.ctx?.state, 'ok:', ok);

      // Play a short "ta-da" test sound immediately so user hears it
      sound._tone(523, 0.1, { type: 'sine', volume: 0.3, attack: 0.01, decay: 0.05, sustain: 0.7, release: 0.08 });
      setTimeout(() => sound._tone(659, 0.1, { type: 'sine', volume: 0.25, attack: 0.01, decay: 0.05, sustain: 0.6, release: 0.08 }), 100);
      setTimeout(() => sound._tone(784, 0.18, { type: 'sine', volume: 0.22, attack: 0.01, decay: 0.08, sustain: 0.6, release: 0.1 }), 200);

      // Start menu music after the test tones
      setTimeout(() => sound.startMusic('menu'), 400);

      this._showLevelSelect();
    };

    keyHandler = async (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        await onClick();
      }
    };

    btn.on('pointertap', onClick);
    window.addEventListener('keydown', keyHandler);
  }

  // ── Poof effect when deleting via trash ─────────────────

  _spawnPoofAt(wx, wy) {
    for (let i = 0; i < 8; i++) {
      const p = new PIXI.Graphics();
      const angle = (i / 8) * Math.PI * 2;
      const r = 6 + Math.random() * 8;
      p.circle(0, 0, r).fill({ color: 0xFFFFFF, alpha: 0.9 });
      p.x = wx + Math.cos(angle) * 10;
      p.y = wy + Math.sin(angle) * 10;
      p.zIndex = Z.particles;
      this.worldContainer.addChild(p);
      const vx = Math.cos(angle) * (60 + Math.random() * 80);
      const vy = Math.sin(angle) * (60 + Math.random() * 80) - 50;
      let life = 0.4;
      const tick = () => {
        const dt = 1 / 60;
        p.x += vx * dt; p.y += vy * dt;
        life -= dt; p.alpha = life / 0.4;
        p.scale.set(life / 0.4);
        if (life > 0) requestAnimationFrame(tick);
        else { this.worldContainer.removeChild(p); p.destroy(); }
      };
      requestAnimationFrame(tick);
    }
  }

  // ── Level Select ────────────────────────────────────────

  _showLevelSelect() {
    this.state = STATE.LEVEL_SELECT;
    this._cleanup();
    if (sound.ready && sound._currentTheme !== 'menu') sound.startMusic('menu');
    this.bgContainer.removeChildren();

    // Draw a nice background
    const bg = new PIXI.Graphics();
    bg.rect(0, 0, GAME_WIDTH, GAME_HEIGHT).fill({ color: 0x0A1628 });
    this.bgContainer.addChild(bg);

    this.levelSelect.show();
  }

  // ── Cleanup ─────────────────────────────────────────────

  _cleanup() {
    // Remove all physics bodies
    this.physics.bodies = [];
    this.physics.waterZones = [];
    this.physics.windZones  = [];

    // Destroy entities
    for (const p of this.platforms)    p.destroy();
    for (const h of this.hazards)      h.destroy();
    for (const g of this.goals)        g.destroy();
    for (const c of this.collectibles) c.destroy();
    for (const n of this.npcs)         n.destroy();

    this.platforms    = [];
    this.hazards      = [];
    this.goals        = [];
    this.collectibles = [];
    this.npcs         = [];

    this.wordSystem?.clear();
    this.interactionSys?.clear();

    if (this.player) {
      this.player.destroy();
      this.player = null;
    }

    this.worldContainer.removeChildren();

    this._risingLavaY = null;
    this._risingLavaGfx = null;
  }

  // ── Input ────────────────────────────────────────────────

  _onKeyDown(e) {
    this._keys[e.key] = true;

    if (this.wordInput?.open) return;

    // Tab → word input
    if (e.key === 'Tab') {
      e.preventDefault();
      this.wordInput?.show();
      sound.buttonClick();
      return;
    }

    // W → word input (when playing)
    if ((e.key === 'Enter' || e.key === 'w') && this.state === STATE.PLAYING && !this.wordInput?.open) {
      if (e.key === 'Enter') {
        this.wordInput?.show();
      }
    }

    // R → retry
    if (e.key === 'r' || e.key === 'R') {
      if (this.state === STATE.PLAYING && (this.player?.dead || this.hud?.isTimeUp())) {
        this.startLevel(this.currentLevelIdx);
      }
    }

    // M → mute toggle
    if (e.key === 'm' || e.key === 'M') {
      const muted = sound.toggleMute();
      this.hud?.showMessage(muted ? '🔇 Muted' : '🔊 Sound On', 1.2);
    }

    // Escape → cancel placement first, then back to menu
    if (e.key === 'Escape') {
      if (this.dragSystem?.isPlacing) {
        this.dragSystem.cancelPlacement();
      } else if (this.state === STATE.PLAYING) {
        this._cleanup();
        this._showLevelSelect();
      }
    }
  }

  _onKeyUp(e) {
    delete this._keys[e.key];
  }

  destroy() {
    window.removeEventListener('keydown', this._boundKeyDown);
    window.removeEventListener('keyup',   this._boundKeyUp);
    this._cleanup();
    this.hud?.destroy();
    this.wordInput?.destroy();
    this.levelSelect?.destroy();
    this.app?.destroy(true);
  }
}
